/**
 * Authentication Service
 * Handles user authentication via Supabase Auth
 */

import { supabase } from '@/config/supabase';
import { validatePassword } from '@/utils/passwordValidation';
import type { User, Session, AuthError } from '@supabase/supabase-js';

export interface AuthUser {
  id: string;
  email: string;
  createdAt: Date;
  shippingAddress?: string;
}

export interface SignUpParams {
  email: string;
  password: string;
}

export interface SignInParams {
  email: string;
  password: string;
}

export interface AuthResult {
  user: AuthUser | null;
  session: Session | null;
  error: AuthError | null;
}

/**
 * Convert Supabase User to AuthUser
 */
function toAuthUser(user: User | null): AuthUser | null {
  if (!user || !user.email) return null;

  return {
    id: user.id,
    email: user.email,
    createdAt: new Date(user.created_at),
    shippingAddress: user.user_metadata?.shipping_address as string | undefined,
  };
}

/**
 * Sign up a new user with email and password
 */
export async function signUp({ email, password }: SignUpParams): Promise<AuthResult> {
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
  });

  return {
    user: toAuthUser(data.user),
    session: data.session,
    error,
  };
}

/**
 * Sign in an existing user with email and password
 */
export async function signIn({ email, password }: SignInParams): Promise<AuthResult> {
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  });

  return {
    user: toAuthUser(data.user),
    session: data.session,
    error,
  };
}

/**
 * Sign out the current user
 */
export async function signOut(): Promise<{ error: AuthError | null }> {
  const { error } = await supabase.auth.signOut();
  return { error };
}

/**
 * Get the current session
 */
export async function getSession(): Promise<{ session: Session | null; user: AuthUser | null }> {
  const { data } = await supabase.auth.getSession();

  return {
    session: data.session,
    user: toAuthUser(data.session?.user ?? null),
  };
}

/**
 * Get the current user
 */
export async function getCurrentUser(): Promise<AuthUser | null> {
  const { data } = await supabase.auth.getUser();
  return toAuthUser(data.user);
}

/**
 * Send password reset email
 */
export async function resetPassword(email: string): Promise<{ error: AuthError | null }> {
  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/reset-password`,
  });

  return { error };
}

/**
 * Update user password
 */
export async function updatePassword(newPassword: string): Promise<{ error: AuthError | null }> {
  const { isAcceptable, messages } = validatePassword(newPassword);
  if (!isAcceptable) {
    return {
      error: {
        name: 'AuthApiError',
        message: `Password too weak. Needs: ${messages.join(', ').toLowerCase()}`,
        status: 422,
      } as AuthError,
    };
  }

  const { error } = await supabase.auth.updateUser({
    password: newPassword,
  });

  return { error };
}

/**
 * Update user profile (shipping address)
 */
export async function updateUserProfile(
  shippingAddress: string
): Promise<{ error: AuthError | null }> {
  const { error } = await supabase.auth.updateUser({
    data: {
      shipping_address: shippingAddress,
    },
  });

  return { error };
}
